// React
import { useCallback, useRef, useState } from 'react'

// Next
import Image from 'next/image'

// Components
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'

// Utils
import { useSession } from '@/utils/auth/user-context'
import { supabaseBrowserClient } from '@/utils/supabase-browser-client'
import { toBase64FromFile } from '@/utils/index'

// External
import { Plus, PlusIcon } from 'lucide-react'
import { compressAccurately } from 'image-conversion'

interface Asset {
  id: string
  url: string
  name: string
  created_at?: string
}

const BUCKET_NAME = 'user-assets'

type AssetGalleryProps = {
  selectAsset: (base64Image: string) => void
  t: (key: string) => string
}

const AssetGallery: React.FC<AssetGalleryProps> = ({ selectAsset, t }) => {
  const session = useSession()
  const [isOpen, setIsOpen] = useState(false)
  const [assets, setAssets] = useState<Asset[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Load assets from Supabase bucket
  const loadExistingAssets = async (): Promise<Asset[]> => {
    const { data: files, error } = await supabaseBrowserClient.storage
      .from(BUCKET_NAME)
      .list(session.user.id)

    if (error) {
      return []
    }

    return files.map((file) => ({
      id: file.id,
      url: supabaseBrowserClient.storage
        .from(BUCKET_NAME)
        .getPublicUrl(`${session.user.id}/${file.name}`).data.publicUrl,
      name: file.name,
      created_at: file.created_at,
    }))
  }

  const handleImageUpload = useCallback(
    async (file: File) => {
      if (!file) return

      // Compress image if needed
      const compressedFile =
        file.size > 150000 ? await compressAccurately(file, 60) : file

      // Upload to Supabase
      const fileName = `${session.user.id}/${Date.now()}-${file.name}`
      const { data, error } = await supabaseBrowserClient.storage
        .from(BUCKET_NAME)
        .upload(fileName, compressedFile)

      if (error) {
        // TODO: show error to user
        return
      }

      // Get public URL
      const {
        data: { publicUrl },
      } = supabaseBrowserClient.storage.from(BUCKET_NAME).getPublicUrl(fileName)

      selectAsset(publicUrl)
      setIsOpen(false)
      loadAssets() // Refresh the asset list
    },
    [selectAsset, t]
  )

  const loadAssets = async () => {
    const loadedAssets = await loadExistingAssets()
    setAssets(loadedAssets)
  }

  if (!session) {
    return (
      <>
        <Input
          type='file'
          accept='image/*'
          onChange={async (e) => {
            const file = e.target.files?.[0]
            if (!!file) {
              const base64String = await toBase64FromFile(file)
              selectAsset(base64String)
            }
          }}
          className='hidden'
          id='direct-asset-upload'
          ref={fileInputRef}
        />
        <Button
          variant='default'
          className='min-w-[120px] flex-1'
          onClick={() => fileInputRef.current?.click()}
        >
          <Plus className='mr-2 h-4 w-4' aria-hidden='true' />
          {t('buttons.addImage.label')}
        </Button>
      </>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant='default'
          className='min-w-[120px] flex-1'
          onClick={() => {
            setIsOpen(true)
            loadAssets()
          }}
        >
          <PlusIcon className='mr-2 h-4 w-4' aria-hidden='true' />
          {t('buttons.addImage.label')}
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[800px]'>
        <DialogHeader>
          <DialogTitle>{t('assetsDialog.title')}</DialogTitle>
          <DialogDescription>{t('assetsDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className='grid gap-4'>
          <div className='grid-col-2 grid gap-3 md:grid-cols-4'>
            <div className='flex justify-end'>
              <Input
                type='file'
                accept='image/*'
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    handleImageUpload(file)
                  }
                }}
                className='hidden'
                id='asset-upload'
              />
              <label htmlFor='asset-upload' className='h-full w-full'>
                <Button variant='outline' className='h-full w-full' asChild>
                  <span>
                    <PlusIcon className='mr-2 h-4 w-4' aria-hidden='true' />
                    {t('buttons.uploadAsset.label')}
                  </span>
                </Button>
              </label>
            </div>
            {assets.map((asset) => (
              <div
                key={asset.id}
                className='relative aspect-square cursor-pointer hover:opacity-80'
                onClick={() => {
                  selectAsset(asset.url)
                  setIsOpen(false)
                }}
              >
                <Image
                  src={asset.url}
                  alt={asset.name}
                  fill
                  className='rounded-lg object-cover'
                />
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export { AssetGallery }
